<template>
  <div class="min-h-screen bg-gray-50 py-12">
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Breadcrumb -->
      <nav class="mb-8">
        <ol class="flex flex-wrap items-center space-x-2 text-sm text-gray-500">
          <li><router-link to="/" class="hover:text-blue-600">Trang chủ</router-link></li>
          <li>/</li>
          <li><router-link to="/blog" class="hover:text-blue-600">Blog</router-link></li>
          <li>/</li>
          <li class="text-gray-900">DNS-over-HTTPS/TLS</li>
        </ol>
      </nav>

      <!-- Article Header -->
      <header class="mb-8">
        <h1 class="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Bảo vệ quyền riêng tư DNS: Hướng dẫn cài đặt DoH/DoT 2025</h1>
        <div class="flex items-center text-sm text-gray-500 mb-6">
          <span>—</span>
          <span class="mx-2">•</span>
          <span>7 phút đọc</span>
          <span class="mx-2">•</span>
          <span>Riêng tư & Bảo mật</span>
        </div>
        <div class="h-48 sm:h-56 lg:h-64 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
          <svg class="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
        </div>
      </header>

      <!-- Article Content -->
      <article class="bg-white rounded-lg shadow-lg p-4 sm:p-6 lg:p-8">
        <div class="prose prose-lg max-w-none">
          <h2>DNS Privacy là gì và tại sao quan trọng?</h2>
          <p>DNS (Domain Name System) là "danh bạ" của internet, chuyển đổi tên miền thành địa chỉ IP. Tuy nhiên, truy vấn DNS truyền thống không được mã hóa, có nghĩa là:</p>
          
          <div class="bg-red-50 p-4 sm:p-6 rounded-lg my-6">
            <h3 class="text-xl font-semibold mb-4 text-red-800">⚠️ Rủi ro bảo mật DNS</h3>
            <ul class="space-y-2 text-red-700">
              <li>• ISP có thể theo dõi mọi website bạn truy cập</li>
              <li>• Chính phủ có thể chặn hoặc giám sát truy cập</li>
              <li>• Tin tặc có thể chặn và chuyển hướng truy cập</li>
              <li>• Dữ liệu có thể bị bán cho bên thứ 3</li>
            </ul>
          </div>

          <h2>DoH và DoT: Giải pháp bảo vệ DNS</h2>
          <div class="grid md:grid-cols-2 gap-6 my-6">
            <div class="bg-blue-50 p-4 rounded-lg">
              <h3 class="font-semibold text-blue-800 mb-2">🔒 DoH (DNS over HTTPS)</h3>
              <ul class="text-sm text-blue-700 space-y-1">
                <li>• Sử dụng HTTPS (cổng 443)</li>
                <li>• Khó phát hiện và chặn</li>
                <li>• Tương thích tốt với firewall</li>
                <li>• Hỗ trợ rộng rãi</li>
              </ul>
            </div>
            <div class="bg-green-50 p-4 rounded-lg">
              <h3 class="font-semibold text-green-800 mb-2">🔐 DoT (DNS over TLS)</h3>
              <ul class="text-sm text-green-700 space-y-1">
                <li>• Sử dụng TLS (cổng 853)</li>
                <li>• Dễ phát hiện hơn DoH</li>
                <li>• Hiệu suất tốt hơn</li>
                <li>• Phù hợp với enterprise</li>
              </ul>
            </div>
          </div>

          <h2>1. Cài đặt DoH/DoT trên Windows 11</h2>
          <div class="bg-gray-50 p-4 sm:p-6 rounded-lg my-6">
            <h3 class="font-semibold mb-4">📋 Hướng dẫn từng bước</h3>
            <ol class="space-y-3">
              <li><strong>Mở Settings:</strong> Nhấn <kbd class="bg-gray-200 px-2 py-1 rounded">Win + I</kbd></li>
              <li><strong>Network & Internet:</strong> Chọn "Network & Internet"</li>
              <li><strong>Wi-Fi/Ethernet:</strong> Chọn kết nối hiện tại</li>
              <li><strong>Properties:</strong> Nhấn "Properties"</li>
              <li><strong>Edit DNS:</strong> Tìm và nhấn "Edit" trong phần DNS</li>
              <li><strong>Manual:</strong> Chuyển từ "Automatic" sang "Manual"</li>
              <li><strong>Add DNS:</strong> Thêm DNS server (ví dụ: 1.1.1.1)</li>
              <li><strong>Enable DoH:</strong> Bật "DNS over HTTPS"</li>
            </ol>
          </div>

          <h3>DNS Servers được khuyến nghị:</h3>
          <div class="grid md:grid-cols-2 gap-4 my-4">
            <div class="border rounded-lg p-4">
              <h4 class="font-semibold text-blue-600">Cloudflare (1.1.1.1)</h4>
              <p class="text-sm text-gray-600">Nhanh nhất, bảo mật cao, không log</p>
              <code class="text-xs">1.1.1.1, 1.0.0.1</code>
            </div>
            <div class="border rounded-lg p-4">
              <h4 class="font-semibold text-green-600">Google (8.8.8.8)</h4>
              <p class="text-sm text-gray-600">Ổn định, hỗ trợ tốt</p>
              <code class="text-xs">8.8.8.8, 8.8.4.4</code>
            </div>
            <div class="border rounded-lg p-4">
              <h4 class="font-semibold text-purple-600">Quad9 (9.9.9.9)</h4>
              <p class="text-sm text-gray-600">Chặn malware, bảo mật</p>
              <code class="text-xs">9.9.9.9, 149.112.112.112</code>
            </div>
            <div class="border rounded-lg p-4">
              <h4 class="font-semibold text-orange-600">OpenDNS (208.67.222.222)</h4>
              <p class="text-sm text-gray-600">Tùy chỉnh cao, parental control</p>
              <code class="text-xs">208.67.222.222, 208.67.220.220</code>
            </div>
          </div>

          <h2>2. Cài đặt trên macOS</h2>
          <div class="bg-gray-50 p-4 sm:p-6 rounded-lg my-6">
            <h3 class="font-semibold mb-4">🍎 Hướng dẫn macOS</h3>
            <ol class="space-y-3">
              <li><strong>System Preferences:</strong> Mở "System Preferences"</li>
              <li><strong>Network:</strong> Chọn "Network"</li>
              <li><strong>Advanced:</strong> Nhấn "Advanced..."</li>
              <li><strong>DNS Tab:</strong> Chuyển sang tab "DNS"</li>
              <li><strong>Add Servers:</strong> Thêm DNS servers</li>
              <li><strong>Apply:</strong> Nhấn "OK" và "Apply"</li>
            </ol>
          </div>

          <h3>Terminal Command (macOS):</h3>
          <pre class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg overflow-x-auto"><code># Kiểm tra DNS hiện tại
scutil --dns

# Thay đổi DNS qua Terminal
sudo networksetup -setdnsservers Wi-Fi 1.1.1.1 1.0.0.1

# Reset về DNS mặc định
sudo networksetup -setdnsservers Wi-Fi "Empty"</code></pre>

          <h2>3. Cài đặt trên Android (Private DNS)</h2>
          <div class="bg-gray-50 p-4 sm:p-6 rounded-lg my-6">
            <h3 class="font-semibold mb-4">📱 Hướng dẫn Android</h3>
            <ol class="space-y-3">
              <li><strong>Settings:</strong> Mở "Settings"</li>
              <li><strong>Network & Internet:</strong> Chọn "Network & internet"</li>
              <li><strong>Private DNS:</strong> Tìm và chọn "Private DNS"</li>
              <li><strong>Private DNS Provider:</strong> Chọn "Private DNS provider hostname"</li>
              <li><strong>Enter Hostname:</strong> Nhập một trong các hostname sau:</li>
            </ol>
          </div>

          <h3>Private DNS Hostnames:</h3>
          <div class="grid md:grid-cols-2 gap-4 my-4">
            <div class="border rounded-lg p-4">
              <h4 class="font-semibold">Cloudflare</h4>
              <code class="text-sm">one.one.one.one</code>
              <p class="text-xs text-gray-600 mt-1">Nhanh nhất, bảo mật cao</p>
            </div>
            <div class="border rounded-lg p-4">
              <h4 class="font-semibold">Google</h4>
              <code class="text-sm">dns.google</code>
              <p class="text-xs text-gray-600 mt-1">Ổn định, tin cậy</p>
            </div>
            <div class="border rounded-lg p-4">
              <h4 class="font-semibold">Quad9</h4>
              <code class="text-sm">dns.quad9.net</code>
              <p class="text-xs text-gray-600 mt-1">Chặn malware</p>
            </div>
            <div class="border rounded-lg p-4">
              <h4 class="font-semibold">AdGuard</h4>
              <code class="text-sm">dns.adguard.com</code>
              <p class="text-xs text-gray-600 mt-1">Chặn quảng cáo</p>
            </div>
          </div>

          <h2>4. Cài đặt trên iOS</h2>
          <div class="bg-gray-50 p-4 sm:p-6 rounded-lg my-6">
            <h3 class="font-semibold mb-4">📱 Hướng dẫn iOS</h3>
            <p class="text-sm text-gray-600 mb-4">iOS không hỗ trợ Private DNS trực tiếp, nhưng có thể sử dụng:</p>
            <ol class="space-y-3">
              <li><strong>VPN Apps:</strong> Sử dụng VPN có hỗ trợ DoH/DoT</li>
              <li><strong>DNS Apps:</strong> Cài đặt app như 1.1.1.1, Cloudflare WARP</li>
              <li><strong>Router Level:</strong> Cấu hình DNS trên router</li>
            </ol>
          </div>

          <h2>5. Kiểm tra và xác minh</h2>
          <div class="bg-green-50 p-4 sm:p-6 rounded-lg my-6">
            <h3 class="font-semibold mb-4 text-green-800">✅ Cách kiểm tra DoH/DoT hoạt động</h3>
            <ol class="space-y-3 text-green-700">
              <li><strong>Cloudflare Test:</strong> Truy cập <a href="https://1.1.1.1/help" class="text-blue-600 underline">https://1.1.1.1/help</a></li>
              <li><strong>DNS Leak Test:</strong> Kiểm tra tại <a href="https://dnsleaktest.com" class="text-blue-600 underline">dnsleaktest.com</a></li>
              <li><strong>Command Line:</strong> Sử dụng <code>nslookup</code> hoặc <code>dig</code></li>
              <li><strong>Browser DevTools:</strong> Kiểm tra Network tab</li>
            </ol>
          </div>

          <h2>6. Troubleshooting</h2>
          <div class="bg-yellow-50 p-4 sm:p-6 rounded-lg my-6">
            <h3 class="font-semibold mb-4 text-yellow-800">⚠️ Các vấn đề thường gặp</h3>
            <div class="space-y-4">
              <div>
                <h4 class="font-medium">DoH không hoạt động:</h4>
                <ul class="text-sm space-y-1 ml-4">
                  <li>• Kiểm tra firewall/antivirus</li>
                  <li>• Thử DNS server khác</li>
                  <li>• Restart network adapter</li>
                </ul>
              </div>
              <div>
                <h4 class="font-medium">Tốc độ chậm:</h4>
                <ul class="text-sm space-y-1 ml-4">
                  <li>• Chọn DNS server gần nhất</li>
                  <li>• Kiểm tra kết nối internet</li>
                  <li>• Thử DoT thay vì DoH</li>
                </ul>
              </div>
              <div>
                <h4 class="font-medium">Một số website không load:</h4>
                <ul class="text-sm space-y-1 ml-4">
                  <li>• Kiểm tra DNS server có block không</li>
                  <li>• Thử DNS server khác</li>
                  <li>• Kiểm tra website có bị chặn không</li>
                </ul>
              </div>
            </div>
          </div>

          <h2>7. Best Practices</h2>
          <div class="bg-blue-50 p-4 sm:p-6 rounded-lg my-6">
            <h3 class="font-semibold mb-4 text-blue-800">💡 Lời khuyên bảo mật</h3>
            <ul class="space-y-2 text-blue-700">
              <li>• <strong>Chọn DNS uy tín:</strong> Cloudflare, Google, Quad9</li>
              <li>• <strong>Kiểm tra định kỳ:</strong> Test DNS leak mỗi tháng</li>
              <li>• <strong>Backup DNS:</strong> Có sẵn DNS dự phòng</li>
              <li>• <strong>Router Level:</strong> Cấu hình DNS cho toàn mạng</li>
              <li>• <strong>Mobile Security:</strong> Sử dụng VPN + DoH/DoT</li>
            </ul>
          </div>

          <div class="bg-gray-50 p-6 rounded-lg mt-8">
            <h3 class="font-semibold mb-3">🔒 Tóm tắt</h3>
            <p class="text-sm text-gray-700">DoH/DoT là bước quan trọng để bảo vệ quyền riêng tư trực tuyến. Mặc dù không hoàn toàn ẩn danh, nhưng chúng ngăn chặn việc theo dõi DNS và tăng cường bảo mật. Kết hợp với VPN sẽ tạo ra lớp bảo vệ mạnh mẽ hơn.</p>
          </div>
        </div>
      </article>

      <!-- Back Button -->
      <div class="mt-8 text-center">
        <router-link 
          to="/blog" 
          class="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors duration-200 text-sm sm:text-base"
        >
          <svg class="mr-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
          </svg>
          Quay lại Blog
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useHead } from '@vueuse/head'
const title = 'Bảo vệ quyền riêng tư DNS: Hướng dẫn cài đặt DoH/DoT 2025'
const desc  = 'Hướng dẫn chi tiết cài đặt DNS over HTTPS/TLS (DoH/DoT) trên Windows, macOS, Android. Bảo vệ quyền riêng tư DNS và tăng cường bảo mật trực tuyến'
const url   = 'https://webservices.vn/blog/dns-over-https-tls'

useHead({
  title,
  meta: [
    { name: 'description', content: desc },
    { property: 'og:type', content: 'article' },
    { property: 'og:title', content: title },
    { property: 'og:description', content: desc },
    { property: 'og:url', content: url },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: desc }
  ],
  link: [{ rel: 'canonical', href: url }]
})
</script>
